import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaUsers,
  FaCheckCircle,
  FaDollarSign,
  FaPlayCircle,
  FaQuestionCircle,
  FaStar,
  FaMobileAlt,
  FaTasks,
  FaCut,
  FaClinicMedical,
  FaShoppingCart,
  FaBriefcase,
} from "react-icons/fa";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0 },
};

const About = () => {
  const navigate = useNavigate();

  // Inject JSON-LD structured data for Google rich results
  useEffect(() => {
    document.title =
      "About CustomerQueue — Queue & Task Management App | $5/month";

    const schema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "SoftwareApplication",
          name: "CustomerQueue",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web, iOS, Android",
          url: "https://customerqueue.com",
          description:
            "CustomerQueue is an affordable, easy-to-use customer queue management and task management app for barbers, salons, clinics, retail stores, and teams. Manage walk-in customer queues and priority task lists — all on one screen.",
          offers: {
            "@type": "Offer",
            price: "5.00",
            priceCurrency: "USD",
            priceValidUntil: "2027-12-31",
          },
          featureList: [
            "Customer self-check-in",
            "Real-time queue management",
            "Priority task management",
            "Works on any device",
            "No extra hardware needed",
          ],
          screenshot: "https://customerqueue.com/applogo.png",
        },
        {
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "What is CustomerQueue?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "CustomerQueue is a web-based queue management and task management app. It lets customers self-check-in on one side of a screen while the business owner manages the queue on the other side — all on a single device with no extra hardware needed.",
              },
            },
            {
              "@type": "Question",
              name: "How much does CustomerQueue cost?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "CustomerQueue costs just $5 per month. There are no setup fees, no contracts, and no hidden charges.",
              },
            },
            {
              "@type": "Question",
              name: "Can CustomerQueue be used for task management?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes! CustomerQueue includes a Task Queue Mode where you can add tasks with High, Medium, or Low priority. Tasks are automatically sorted so your team always works on the most important item first.",
              },
            },
            {
              "@type": "Question",
              name: "What businesses can use CustomerQueue?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "CustomerQueue is perfect for barber shops, hair salons, nail salons, clinics, GP surgeries, retail stores, repair shops, government service desks, support teams, and any business or team that manages a waiting list or task backlog.",
              },
            },
            {
              "@type": "Question",
              name: "Do customers need to download an app?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "No. Customers check in directly on the screen provided by the business. There is nothing to download and no account is needed for customers.",
              },
            },
            {
              "@type": "Question",
              name: "Does CustomerQueue work on mobile?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. CustomerQueue is fully responsive and works on any smartphone, tablet, laptop, or desktop. You only need one device and a web browser.",
              },
            },
          ],
        },
      ],
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "about-schema";
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    const metaDesc = document.querySelector("meta[name='description']");
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "CustomerQueue is an affordable queue management and task management app for barbers, salons, clinics, retail, and teams. Only $5/month. Manage customer walk-ins and priority tasks on one screen — no extra devices needed."
      );
    }

    return () => {
      const el = document.getElementById("about-schema");
      if (el) el.remove();
    };
  }, []);

  const useCases = [
    {
      icon: <FaCut className="text-3xl text-blue-500" />,
      title: "Barber Shops",
      desc: "Let walk-in customers check themselves in while you focus on the cut. No more shouting names across the room.",
    },
    {
      icon: <FaStar className="text-3xl text-pink-500" />,
      title: "Hair & Nail Salons",
      desc: "Manage multiple stylists and walk-ins from a single tablet at the reception desk.",
    },
    {
      icon: <FaClinicMedical className="text-3xl text-green-500" />,
      title: "Clinics & GP Surgeries",
      desc: "Streamline patient check-in and reduce waiting-room confusion with a clear live queue display.",
    },
    {
      icon: <FaShoppingCart className="text-3xl text-orange-500" />,
      title: "Retail & Service Desks",
      desc: "Perfect for deli counters, post offices, phone repair shops, and any service where customers wait their turn.",
    },
    {
      icon: <FaTasks className="text-3xl text-purple-500" />,
      title: "Teams & Task Boards",
      desc: "Switch to Task Mode to manage your team's daily work. Add High, Medium, or Low priority tasks and let the app sort what comes first.",
    },
    {
      icon: <FaBriefcase className="text-3xl text-teal-500" />,
      title: "Support & Help Desks",
      desc: "Track incoming support requests by priority. Know what to tackle next without a spreadsheet or complex project tool.",
    },
  ];

  const features = [
    {
      icon: <FaUsers className="text-blue-500 text-2xl" />,
      title: "Customer Self-Check-In",
      desc: "Customers type their name on the left side of the screen and instantly receive a queue number. No paper tickets, no receptionist needed.",
    },
    {
      icon: <FaCheckCircle className="text-green-500 text-2xl" />,
      title: "Live Queue Management",
      desc: "See the full waiting list on the right side of the same screen. Tap Serve Now, mark customers as served, or remove them with one click.",
    },
    {
      icon: <FaTasks className="text-purple-500 text-2xl" />,
      title: "Priority Task Queue",
      desc: "Switch to Task Mode and add tasks with 🔴 High, 🟡 Medium, or 🔵 Low priority. High-priority items always bubble to the top automatically.",
    },
    {
      icon: <FaMobileAlt className="text-orange-500 text-2xl" />,
      title: "Works on Any Device",
      desc: "A single tablet, phone, laptop, or desktop is all you need. No additional screens, no special hardware, no installation.",
    },
    {
      icon: <FaDollarSign className="text-yellow-500 text-2xl" />,
      title: "Only $5/Month",
      desc: "Flat fee — no contracts, no setup costs, no per-user charges. Cancel any time.",
    },
    {
      icon: <FaPlayCircle className="text-teal-500 text-2xl" />,
      title: "Live Demo Available",
      desc: "Try the full app before signing up. The demo auto-resets every 2 minutes so you can explore both Queue Mode and Task Mode freely.",
    },
  ];

  const faqs = [
    {
      q: "What is CustomerQueue?",
      a: "CustomerQueue is a web-based app that combines customer queue management and priority task management in one place. Businesses use it to handle walk-in customers and waiting lists; teams use it to organise and prioritise daily tasks — all on one screen, one device.",
    },
    {
      q: "How much does it cost?",
      a: "Just $5 per month. No setup fees, no long-term contracts, no hidden charges. You can cancel at any time from your account settings.",
    },
    {
      q: "Do I need any special hardware or equipment?",
      a: "No. All you need is one device — a tablet, phone, laptop, or desktop — with a web browser. Customers check in on the same screen you use to manage the queue.",
    },
    {
      q: "Do customers need to create an account or download anything?",
      a: "No. Customers simply type their name on the check-in screen you provide. There is nothing to download and no account needed for customers.",
    },
    {
      q: "Can I use it as a task manager for my team?",
      a: "Yes! Task Queue Mode lets you add tasks with High, Medium, or Low priority. The app automatically sorts pending tasks so High priority ones always appear first. You can switch between Customer Mode and Task Mode instantly with one button.",
    },
    {
      q: "What happens if I close the browser tab?",
      a: "Your queue data is stored securely in your account. You can reopen the app on any device and your queue will be exactly as you left it.",
    },
    {
      q: "Is CustomerQueue suitable for a busy salon or barber shop?",
      a: "Absolutely. You can serve multiple customers simultaneously — each gets their own queue number. The app clearly shows who is being served and who is next. It has been designed specifically for fast-paced, walk-in environments.",
    },
    {
      q: "Can I try it before subscribing?",
      a: "Yes. Click Try Demo on the home page to use the full app in demo mode. No sign-up required. The demo resets automatically every 2 minutes and includes pre-loaded examples for both Customer Queue Mode and Task Queue Mode.",
    },
    {
      q: "How do I get started?",
      a: "Click Register, create a free account, and you're ready to go in under 2 minutes. Log in on your device, set up your business name, and start managing your queue straight away.",
    },
  ];

  return (
    <div className="bg-gray-50 overflow-x-hidden">

      {/* ── Hero ── */}
      <section className="bg-white py-16 shadow-sm">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block bg-blue-100 text-blue-700 text-sm font-semibold px-4 py-1 rounded-full mb-4 uppercase tracking-wide">
              About CustomerQueue
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold text-gray-900 mb-5 leading-tight">
              The Simplest Way to Manage{" "}
              <span className="text-blue-600">Customer Queues</span> &amp;{" "}
              <span className="text-purple-600">Team Tasks</span> — All on One Screen
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto mb-8">
              CustomerQueue is an affordable, easy-to-use web app for
              barbers, salons, clinics, retail stores, and teams. Eliminate
              paper tickets, reduce confusion, and keep your customers and
              colleagues happy — for just{" "}
              <span className="font-bold text-green-600">$5 a month</span>.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("/register")}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold text-lg shadow"
              >
                Get Started Free
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("/demo-queue")}
                className="border-2 border-green-600 text-green-700 hover:bg-green-50 px-8 py-3 rounded-lg font-semibold text-lg flex items-center justify-center gap-2"
              >
                <FaPlayCircle /> Try Live Demo
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── What Is It ── */}
      <section className="py-14 bg-gray-50">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4">
              What Is CustomerQueue?
            </h2>
            <p className="text-gray-600 text-lg mb-4">
              <strong>CustomerQueue</strong> is a web-based queue management system and task
              manager built for small businesses and teams that need a simple, affordable
              solution. Unlike expensive enterprise ticketing systems, CustomerQueue
              runs entirely in your web browser — no app to download, no extra
              hardware to buy, no IT setup required.
            </p>
            <p className="text-gray-600 text-lg mb-4">
              It works in two modes. In <strong>Customer Queue Mode</strong>,
              customers walk up to your tablet or screen, type their name, and
              instantly receive a queue number. You manage the live queue on the
              right side of the same screen — see who's waiting, serve them,
              mark them as done, or remove them. Everything updates in real time.
            </p>
            <p className="text-gray-600 text-lg">
              In <strong>Task Queue Mode</strong>, you or your team add tasks
              with a priority level — 🔴 High, 🟡 Medium, or 🔵 Low. The app
              automatically sorts tasks so the most urgent work is always at the
              top. It's a lightweight, distraction-free task board that anyone
              can use without training.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Use Cases ── */}
      <section className="py-14 bg-white">
        <div className="container mx-auto px-6 max-w-5xl">
          <motion.h2
            className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2 text-center"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Who Uses CustomerQueue?
          </motion.h2>
          <p className="text-center text-gray-500 mb-10">
            Any business or team that manages a waiting list or a to-do list.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {useCases.map((item, i) => (
              <motion.article
                key={i}
                className="bg-gray-50 rounded-xl p-6 shadow-sm hover:shadow-md transition"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
              >
                <div className="mb-3">{item.icon}</div>
                <h3 className="text-lg font-bold text-gray-800 mb-1">{item.title}</h3>
                <p className="text-gray-500 text-sm">{item.desc}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="py-14 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="container mx-auto px-6 max-w-5xl">
          <motion.h2
            className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2 text-center"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Key Features
          </motion.h2>
          <p className="text-center text-gray-500 mb-10">
            Everything you need. Nothing you don't.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <motion.div
                key={i}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
              >
                <div className="mb-3">{f.icon}</div>
                <h3 className="text-base font-bold text-gray-800 mb-1">{f.title}</h3>
                <p className="text-gray-500 text-sm">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="py-14 bg-white">
        <div className="container mx-auto px-6 max-w-3xl">
          <motion.h2
            className="text-2xl sm:text-3xl font-bold text-gray-800 mb-10 text-center"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            How It Works — 3 Simple Steps
          </motion.h2>
          <ol className="space-y-8">
            {[
              {
                step: "1",
                color: "bg-blue-600",
                title: "Create a Free Account",
                desc: "Sign up in under 2 minutes at CustomerQueue.com. Enter your business name and you're ready to go. One account works across all your devices.",
              },
              {
                step: "2",
                color: "bg-green-600",
                title: "Log In on Your Device",
                desc: "Open the app on a tablet, phone, or laptop. Customers self-check-in on the left side of the screen. Your queue management panel is on the right — always visible, always in sync.",
              },
              {
                step: "3",
                color: "bg-purple-600",
                title: "Serve Customers or Complete Tasks",
                desc: "Tap Serve Now to call the next customer, or Start Task to begin working on a task. Mark items as served or done. The queue updates instantly. Switch between Customer Mode and Task Mode any time.",
              },
            ].map((item, i) => (
              <motion.li
                key={i}
                className="flex gap-5 items-start"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.12 }}
              >
                <span
                  className={`${item.color} text-white text-xl font-extrabold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 shadow`}
                >
                  {item.step}
                </span>
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-1">{item.title}</h3>
                  <p className="text-gray-500">{item.desc}</p>
                </div>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section className="py-14 bg-gray-50">
        <div className="container mx-auto px-6 max-w-xl text-center">
          <motion.h2
            className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Simple, Honest Pricing
          </motion.h2>
          <motion.div
            className="bg-white rounded-2xl shadow-lg p-10 border-t-4 border-green-500"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-6xl font-extrabold text-green-600 mb-1">$5</p>
            <p className="text-gray-400 text-lg mb-6">per month · cancel any time</p>
            <ul className="text-left space-y-3 mb-8 text-gray-600">
              {[
                "Unlimited queue entries per day",
                "Customer Queue Mode + Task Queue Mode",
                "Works on any device — phone, tablet, laptop",
                "Real-time updates, no refresh needed",
                "No extra hardware or software required",
                "No contracts, no setup fees",
              ].map((point, i) => (
                <li key={i} className="flex items-start gap-2">
                  <FaCheckCircle className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate("/register")}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-4 rounded-xl text-lg shadow"
            >
              Start for $5/month →
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-14 bg-white">
        <div className="container mx-auto px-6 max-w-3xl">
          <motion.h2
            className="text-2xl sm:text-3xl font-bold text-gray-800 mb-10 text-center flex items-center justify-center gap-2"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <FaQuestionCircle className="text-blue-500" /> Frequently Asked Questions
          </motion.h2>
          <dl className="space-y-6">
            {faqs.map((faq, i) => (
              <motion.div
                key={i}
                className="bg-gray-50 rounded-xl p-6 shadow-sm"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: i * 0.06 }}
              >
                <dt className="font-bold text-gray-800 mb-2 text-base">{faq.q}</dt>
                <dd className="text-gray-500 text-sm leading-relaxed">{faq.a}</dd>
              </motion.div>
            ))}
          </dl>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-center">
        <div className="container mx-auto px-6 max-w-2xl">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl sm:text-4xl font-extrabold mb-4">
              Ready to Stop Managing Queues Manually?
            </h2>
            <p className="text-blue-100 text-lg mb-8">
              Join businesses and teams already using CustomerQueue to save time,
              reduce stress, and serve more customers every day — for just $5/month.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("/register")}
                className="bg-white text-blue-700 font-bold px-8 py-3 rounded-xl text-lg shadow hover:bg-blue-50"
              >
                Create Free Account
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("/demo-queue")}
                className="border-2 border-white text-white font-bold px-8 py-3 rounded-xl text-lg hover:bg-white hover:text-blue-700 transition flex items-center justify-center gap-2"
              >
                <FaPlayCircle /> Try Demo First
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Footer note ── */}
      <footer className="bg-gray-900 text-gray-400 py-6 text-center text-sm">
        <p>
          © {new Date().getFullYear()} CustomerQueue.com · Queue smarter ·{" "}
          <button
            onClick={() => navigate("/terms-and-conditions")}
            className="underline hover:text-white transition"
          >
            Terms &amp; Conditions
          </button>
        </p>
      </footer>
    </div>
  );
};

export default About;
